import { Button, radius } from '@alison/ui';
import { useTenant } from '@alison/whitelabel/react';
import EmailIcon from '@mui/icons-material/Email';
import TuneIcon from '@mui/icons-material/Tune';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { GLYPH_SX, MetaRow, StatusLabel, statusChipSx } from './channelCardShared.js';
import type { EmailData } from './notifications.types.js';
import { OverrideChip } from './OverrideChip.js';

export function EmailChannelCard({
  data,
  canManage,
  onManage,
}: {
  data: EmailData;
  canManage: boolean;
  onManage: () => void;
}): ReactNode {
  const { providerName } = useTenant();
  const connected = data.status === 'connected' || data.status === 'needs-attention';
  const needsAttention = data.status === 'needs-attention';

  return (
    <Stack
      sx={{
        flex: 1,
        bgcolor: connected ? 'background.paper' : 'background.page',
        border: '1px solid',
        borderColor: needsAttention ? 'warning.main' : 'divider',
        borderRadius: `${radius.xl}px`,
        boxShadow: 5,
        p: 4,
        gap: 3,
        minHeight: 258,
      }}
    >
      <Stack direction="row" sx={{ gap: 3, alignItems: 'flex-start' }}>
        <Box sx={{ ...GLYPH_SX, bgcolor: 'primary.main' }}>
          <EmailIcon sx={{ color: 'common.white', fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'text.primary' }}>
              Email
            </Typography>
            <Chip
              size="small"
              label={<StatusLabel status={data.status} />}
              sx={statusChipSx(data.status)}
            />
          </Stack>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>
            Email your team via {providerName}'s verified sender.
          </Typography>
        </Box>
      </Stack>

      {connected && (
        <Stack sx={{ gap: 1, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
          <MetaRow label="Sender">{data.sender}</MetaRow>
          <MetaRow label="Recipients">
            <OverrideChip label="All account emails" />
            {data.recipients.map((recipient) => (
              <OverrideChip key={recipient} label={recipient} />
            ))}
            <Box
              component="button"
              onClick={onManage}
              disabled={!canManage}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                height: 22,
                px: 2,
                borderRadius: `${radius.sm}px`,
                border: '1px dashed',
                borderColor: 'divider',
                bgcolor: 'transparent',
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 600,
                color: 'text.secondary',
                '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
              }}
            >
              + Add
            </Box>
          </MetaRow>
        </Stack>
      )}

      {!connected && (
        <Typography
          sx={{
            fontSize: 12,
            color: 'text.secondary',
            pt: 3,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          Email delivery is not available for this workspace yet.
        </Typography>
      )}

      <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
        {connected && canManage && (
          <Button size="small" variant="outlined" startIcon={<TuneIcon />} onClick={onManage}>
            Manage
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
